/**
 * Heatmap - GitHub-style activity grid
 *
 * Usage:
 *   <Heatmap
 *     title="Coding Activity"
 *     data={{
 *       cells: [
 *         { date: '2024-01-01', intensity: 2 },
 *         { date: '2024-01-02', intensity: 4 },
 *         ...
 *       ],
 *       weeks: 52
 *     }}
 *   />
 */

import type { BlockProps, HeatmapData } from './types';
import { getHeatmapColor } from './colors';
import './blocks.css';

export interface HeatmapProps extends BlockProps<HeatmapData> {
  /** Cell size in pixels */
  cellSize?: number;
  /** Show day labels */
  showDayLabels?: boolean;
  /** Show month labels */
  showMonthLabels?: boolean;
}

export function Heatmap({
  data,
  title,
  cellSize = 12,
  showDayLabels = true,
  showMonthLabels = true,
  className,
}: HeatmapProps) {
  const { cells, startDate, weeks = 52 } = data;

  if (!cells || cells.length === 0) {
    return (
      <div className={`block-heatmap ${className || ''}`}>
        {title && <div className="block-heatmap-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No activity data
        </div>
      </div>
    );
  }

  // Create a map for quick lookup
  const intensityMap = new Map(cells.map(c => [c.date, c.intensity]));

  // Calculate grid data
  const endDate = new Date();
  const start = startDate ? new Date(startDate) : new Date(endDate.getTime() - weeks * 7 * 24 * 60 * 60 * 1000);

  // Align to Sunday
  start.setDate(start.getDate() - start.getDay());

  // Generate all dates
  const allDates: { date: Date; intensity: number }[] = [];
  const current = new Date(start);

  while (current <= endDate) {
    const dateStr = current.toISOString().split('T')[0];
    allDates.push({
      date: new Date(current),
      intensity: intensityMap.get(dateStr) || 0,
    });
    current.setDate(current.getDate() + 1);
  }

  // Group by week
  const weekGroups: typeof allDates[] = [];
  for (let i = 0; i < allDates.length; i += 7) {
    weekGroups.push(allDates.slice(i, i + 7));
  }

  // Get month labels
  const monthLabels: { month: string; weekIndex: number }[] = [];
  let lastMonth = -1;
  weekGroups.forEach((week, weekIndex) => {
    const firstDay = week[0];
    if (firstDay && firstDay.date.getMonth() !== lastMonth) {
      lastMonth = firstDay.date.getMonth();
      monthLabels.push({
        month: firstDay.date.toLocaleDateString('en-US', { month: 'short' }),
        weekIndex,
      });
    }
  });

  const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
  const gap = 3;

  return (
    <div className={`block-heatmap ${className || ''}`}>
      {title && <div className="block-heatmap-title">{title}</div>}

      <div style={{ overflowX: 'auto' }}>
        {/* Month labels */}
        {showMonthLabels && (
          <div style={{
            position: 'relative',
            height: '16px',
            marginBottom: '4px',
            marginLeft: showDayLabels ? '28px' : '0',
          }}>
            {monthLabels.map((label, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: label.weekIndex * (cellSize + gap),
                  fontSize: '10px',
                  color: 'var(--color-text-muted)',
                }}
              >
                {label.month}
              </div>
            ))}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: `${gap}px`,
          marginTop: showMonthLabels ? '16px' : '0',
        }}>
          {/* Day labels */}
          {showDayLabels && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: `${gap}px`,
              marginRight: '4px',
            }}>
              {dayLabels.map((label, i) => (
                <div
                  key={i}
                  style={{
                    height: cellSize,
                    fontSize: '9px',
                    color: 'var(--color-text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    width: '20px',
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          )}

          {/* Heatmap grid */}
          {weekGroups.map((week, weekIndex) => (
            <div
              key={weekIndex}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: `${gap}px`,
              }}
            >
              {week.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  style={{
                    width: cellSize,
                    height: cellSize,
                    borderRadius: '2px',
                    backgroundColor: getHeatmapColor(day.intensity),
                  }}
                  title={`${day.date.toLocaleDateString()}: ${day.intensity} contributions`}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '12px',
          justifyContent: 'flex-end',
        }}>
          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginRight: '4px' }}>Less</span>
          {[0, 1, 2, 3, 4].map((level) => (
            <div
              key={level}
              style={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                backgroundColor: getHeatmapColor(level),
              }}
            />
          ))}
          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginLeft: '4px' }}>More</span>
        </div>
      </div>
    </div>
  );
}

export default Heatmap;
